import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiEdit3,
  FiFileText,
  FiLayers,
  FiPackage,
  FiRefreshCw,
  FiSend,
  FiShield,
  FiX,
} from 'react-icons/fi';
import { useClaimOffer, useOffer } from '../../api/offers';
import type { ChatMessage } from '../../api/conversations';
import { withEffectiveOfferStatus, type OfferStatus } from '../gigs/my-gigs/types';
import { useOfferNow } from '../gigs/my-gigs/offers/useOfferNow';
import {
  findActivePaymentLink,
  findPendingSettlement,
  getOfferRemainingBalance,
  getPaymentRoles,
  offerPaymentPollInterval,
  paymentLinkPayerTotal,
} from '../gigs/my-gigs/offers/paymentLinks';
import { calculateOfferFees } from '../gigs/my-gigs/offers/offerFees';
import type { OfferDetailAction } from '../gigs/my-gigs/offers/OfferDetailsView';
import { useUserStore } from '../../store/useUserStore';
import { isStripeAccountReady, useStripeAccountStatus } from '../../api/stripe';
import { Tooltip } from '../ui/Tooltip';
import { offerStatusTips, paymentTips } from '../../onboarding/copy';

type BadgeTone = 'blue' | 'amber' | 'green' | 'red' | 'zinc';

const STATUS_BADGE: Record<OfferStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: 'Draft', tone: 'zinc' },
  pending: { label: 'Sent', tone: 'blue' },
  accepted: { label: 'Accepted', tone: 'green' },
  rejected: { label: 'Declined', tone: 'red' },
  withdrawn: { label: 'Withdrawn', tone: 'zinc' },
  expired: { label: 'Expired', tone: 'zinc' },
  changes_requested: { label: 'Changes requested', tone: 'amber' },
};

const BADGE_TONE_CLASS: Record<BadgeTone, string> = {
  blue: 'bg-[#1784FF]/12 text-[#5AA8FF] border-[#1784FF]/25',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  green: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  red: 'bg-red-500/10 text-red-300 border-red-500/25',
  zinc: 'bg-white/[0.05] text-zinc-400 border-white/10',
};

const BADGE_DOT_CLASS: Record<BadgeTone, string> = {
  blue: 'bg-[#5AA8FF]',
  amber: 'bg-amber-300',
  green: 'bg-emerald-300',
  red: 'bg-red-300',
  zinc: 'bg-zinc-500',
};

const CANCELLATION_LABEL: Record<string, string> = {
  flexible: 'Flexible cancellation',
  moderate: 'Moderate cancellation',
  strict: 'Strict cancellation',
};

function money(amount: number, currency?: string): string {
  const code = currency || 'USD';
  const symbol = code === 'EUR' ? '€' : code === 'GBP' ? '£' : '$';
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  return `${symbol}${amount.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function expiresInLabel(iso: string | undefined, now: number) {
  if (!iso) return null;
  const ms = Date.parse(iso) - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return { text: 'Expires in under an hour', urgent: true };
  if (hours < 24) return { text: `Expires in ${hours}h`, urgent: true };
  const days = Math.floor(hours / 24);
  return { text: `Expires in ${days} day${days === 1 ? '' : 's'}`, urgent: false };
}

const DetailChip: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
  urgent?: boolean;
}> = ({ icon, children, urgent }) => (
  <span
    className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-[11.5px] font-medium break-words ${
      urgent
        ? 'border-amber-500/25 bg-amber-500/10 text-amber-300'
        : 'border-white/[0.06] bg-white/[0.04] text-zinc-300'
    }`}
  >
    <span className={`shrink-0 ${urgent ? 'text-amber-300' : 'text-zinc-500'}`}>{icon}</span>
    {children}
  </span>
);

const Notice: React.FC<{
  icon: React.ReactNode;
  tone: 'amber' | 'zinc' | 'green';
  children: React.ReactNode;
}> = ({ icon, tone, children }) => (
  <div
    className={`mx-4 mt-3 flex items-start gap-2 rounded-xl border px-3 py-2 text-[12px] leading-snug ${
      tone === 'amber'
        ? 'border-amber-500/20 bg-amber-500/[0.07] text-amber-200'
        : tone === 'green'
          ? 'border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-200'
          : 'border-white/[0.06] bg-white/[0.03] text-zinc-400'
    }`}
  >
    <span className="mt-[1px] shrink-0">{icon}</span>
    <span className="min-w-0 break-words">{children}</span>
  </div>
);

const primaryButton =
  'inline-flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-[13px] font-bold transition active:scale-[0.98] cursor-pointer';
const secondaryButton =
  'inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] py-2 text-[12.5px] font-semibold transition hover:bg-white/[0.07] active:scale-[0.98] cursor-pointer';

interface OfferChatCardProps {
  message: ChatMessage;
  offerUuid: string;
}

export const OfferChatCard: React.FC<OfferChatCardProps> = ({ message, offerUuid }) => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const currentUserId = user?.id != null ? String(user.id) : '';
  const currentUserEmail = (user?.email || '').toLowerCase();
  const {
    data: offer,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useOffer(offerUuid, {
    refetchInterval: (data) => {
      const paymentPoll = offerPaymentPollInterval(data, currentUserId, currentUserEmail);
      if (paymentPoll) return paymentPoll;
      if (data?.status === 'pending' || data?.status === 'changes_requested') return 20000;
      return false;
    },
  });
  const { data: stripeAccount } = useStripeAccountStatus();
  const now = useOfferNow(offer);
  const effectiveOffer = useMemo(
    () => (offer ? withEffectiveOfferStatus(offer, now) : undefined),
    [offer, now],
  );

  const payload = message.card_payload || {};
  const status = effectiveOffer?.status;
  const title = offer?.title || payload.title || 'Offer';
  const currency =
    offer?.currency || (typeof payload.currency === 'string' ? payload.currency : 'USD');
  const price =
    offer?.price ??
    (payload.offer_amount != null && payload.offer_amount !== ''
      ? Number(payload.offer_amount)
      : undefined);
  const amountLabel = price != null && Number.isFinite(price) ? money(price, currency) : undefined;

  const roles = offer
    ? getPaymentRoles(offer, currentUserId, currentUserEmail, message.conversation_id)
    : null;
  const isCreator = !!roles?.isCreator;
  const isRecipient = !!roles?.isRecipient;
  const isPayer = !!roles?.isPayer;
  const isLinkGenerator = !!roles?.isLinkGenerator;
  const stripeReady = isStripeAccountReady(stripeAccount);
  const fees = price != null && Number.isFinite(price) ? calculateOfferFees(price) : null;
  const awaitingMyAcceptance = status === 'pending' && isRecipient;

  const claimOffer = useClaimOffer();
  const claimAttempted = useRef(false);
  useEffect(() => {
    if (claimAttempted.current) return;
    if (
      !isCreator &&
      offer?.id &&
      offer.recipientUserId == null &&
      offer.recipientEmail &&
      currentUserEmail &&
      offer.recipientEmail.trim().toLowerCase() === currentUserEmail.trim().toLowerCase()
    ) {
      claimAttempted.current = true;
      claimOffer.mutate();
    }
  }, [offer, isCreator, currentUserEmail, claimOffer]);
  const isPaid =
    status === 'accepted' &&
    !!offer &&
    (offer.paymentStatus === 'paid' || getOfferRemainingBalance(offer) <= 0.001);
  const gigOffer = !!offer?.gigUuid;
  const partyName = isCreator
    ? offer?.recipientName || 'the recipient'
    : offer?.creatorName || 'the creator';

  const badge = status
    ? isPaid
      ? { label: 'Paid', tone: 'green' as BadgeTone }
      : awaitingMyAcceptance
        ? { label: 'Awaiting you', tone: 'blue' as BadgeTone }
        : STATUS_BADGE[status]
    : { label: String(payload.status || 'Offer'), tone: 'zinc' as BadgeTone };

  const badgeTip = status
    ? offerStatusTips[isPaid ? 'paid' : awaitingMyAcceptance ? 'awaiting_you' : status]
    : undefined;

  const eyebrow = isRecipient ? 'Offer for you' : isCreator ? 'Your offer' : 'Offer';
  const expiry = status === 'pending' ? expiresInLabel(offer?.expirationDate, now) : null;
  const milestoneCount = offer?.paymentType === 'milestone' ? offer.milestones.length : 0;
  const revisions = offer?.revisionsIncluded ?? 0;

  const open = (action?: OfferDetailAction) => {
    navigate({
      to: '/offers/$offerId',
      params: { offerId: offerUuid },
      search: { action },
    });
  };

  const paymentOpen = !!offer && status === 'accepted' && !isPaid && (isCreator || isRecipient);
  const activeLink = paymentOpen && offer ? findActivePaymentLink(offer) : null;
  const activeLinkTotal = activeLink ? paymentLinkPayerTotal(activeLink) : undefined;
  // TEMP-DISABLED (external payments hidden — chat only): restore pending lookup to re-enable.
  // const pendingSettlement =
  //   paymentOpen && offer ? findPendingSettlement(offer, currentUserId) : null;
  const pendingSettlement = null;

  const showRecipientActions = !!offer && status === 'pending' && isRecipient;
  const showRevise = !!offer && status === 'changes_requested' && isCreator;
  const showSendNew =
    !!offer &&
    (status === 'rejected' || status === 'expired' || status === 'withdrawn') &&
    isCreator;
  // TEMP-DISABLED: external payment confirm/reject hidden — chat only for now.
  // const showSettlementActions = !!pendingSettlement?.waitingOnMe;
  const showSettlementActions = false;
  const showStripeSetup =
    paymentOpen && isLinkGenerator && gigOffer && !stripeReady && !showSettlementActions;
  const showRequestPayment =
    paymentOpen &&
    isLinkGenerator &&
    gigOffer &&
    stripeReady &&
    !activeLink &&
    !showSettlementActions;
  const showPay = paymentOpen && isPayer && !!activeLink && !showSettlementActions;
  const showWaitingForLink = paymentOpen && isPayer && !activeLink && !pendingSettlement;
  const showPaymentRequested = paymentOpen && isLinkGenerator && !!activeLink && !pendingSettlement;
  // TEMP-DISABLED: external payment link hidden — chat only for now.
  // const showOffPlatformLink = paymentOpen && !pendingSettlement;
  const showOffPlatformLink = false;
  const hasPrimaryAction =
    showRecipientActions ||
    showRevise ||
    showSendNew ||
    showSettlementActions ||
    showStripeSetup ||
    showRequestPayment ||
    showPay;

  // TEMP-DISABLED: external payment amount hidden — chat only for now.
  // const settlementAmount = pendingSettlement
  //   ? money(pendingSettlement.payment.amount, currency)
  //   : '';
  const settlementAmount = '';
  // TEMP: keep disabled settlement wiring referenced so lint passes; remove on re-enable.
  void [findPendingSettlement, pendingSettlement, showOffPlatformLink, settlementAmount];

  return (
    <div
      className={`relative w-full min-w-0 rounded-2xl p-[1px] ${
        awaitingMyAcceptance
          ? 'offer-card-glow'
          : 'bg-gradient-to-br from-[#1784FF]/40 via-[#3A3A3C] to-[#00D45A]/30'
      }`}
    >
      <div className="w-full overflow-hidden rounded-[15px] bg-[#17181B] text-[14px] leading-relaxed shadow-lg">
        <div className="flex items-start justify-between gap-3 px-4 pt-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#1784FF]/20 bg-[#1784FF]/10 text-[#5AA8FF]">
              <FiFileText size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-zinc-500">
                {eyebrow}
              </p>
              <p className="truncate text-[15px] font-semibold leading-tight text-white">{title}</p>
            </div>
          </div>
          <Tooltip content={badgeTip} touch="tap" placement="bottom-end">
            <span
              tabIndex={badgeTip ? 0 : undefined}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${BADGE_TONE_CLASS[badge.tone]} ${badgeTip ? 'cursor-help' : ''}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${BADGE_DOT_CLASS[badge.tone]}`} />
              {badge.label}
            </span>
          </Tooltip>
        </div>

        {isLoading && !amountLabel ? (
          <div className="space-y-2 px-4 pt-4 animate-pulse">
            <div className="h-7 w-28 rounded-lg bg-white/[0.06]" />
            <div className="h-3 w-48 rounded bg-white/[0.04]" />
          </div>
        ) : (
          amountLabel && (
            <div className="px-4 pt-3.5">
              <p className="text-[28px] font-bold leading-none tracking-tight text-white">
                {amountLabel}
              </p>
              {fees && (isPayer || isLinkGenerator) && (
                <p className="mt-1.5 text-[12px] text-zinc-400">
                  {isPayer ? (
                    <>
                      You pay{' '}
                      <span className="font-semibold text-zinc-200">
                        {money(fees.creatorTotal, currency)}
                      </span>{' '}
                      including the processing fee
                    </>
                  ) : (
                    <>
                      You receive{' '}
                      <span className="font-semibold text-emerald-300">
                        {money(fees.candidatePayout, currency)}
                      </span>{' '}
                      after Musicade&apos;s commission
                    </>
                  )}
                </p>
              )}
            </div>
          )
        )}

        {offer && (
          <div className="flex flex-wrap gap-1.5 px-4 pt-3.5">
            {offer.startDate && (
              <DetailChip icon={<FiCalendar size={12} />}>Starts {offer.startDate}</DetailChip>
            )}
            {offer.deliveryDate && (
              <DetailChip icon={<FiPackage size={12} />}>Due {offer.deliveryDate}</DetailChip>
            )}
            {milestoneCount > 0 && (
              <DetailChip icon={<FiLayers size={12} />}>
                {milestoneCount} milestone{milestoneCount === 1 ? '' : 's'}
              </DetailChip>
            )}
            {revisions > 0 && (
              <DetailChip icon={<FiRefreshCw size={12} />}>
                {revisions >= 99
                  ? 'Unlimited revisions'
                  : `${revisions} revision${revisions === 1 ? '' : 's'}`}
              </DetailChip>
            )}
            {offer.cancellationPolicy && (
              <DetailChip icon={<FiShield size={12} />}>
                {CANCELLATION_LABEL[offer.cancellationPolicy] ?? offer.cancellationPolicy}
              </DetailChip>
            )}
            {expiry && (
              <DetailChip icon={<FiClock size={12} />} urgent={expiry.urgent}>
                {expiry.text}
              </DetailChip>
            )}
          </div>
        )}

        {!offer && isError && (
          <Notice icon={<FiAlertCircle size={13} />} tone="amber">
            We couldn&apos;t load this offer.{' '}
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="font-semibold underline underline-offset-2 hover:text-white cursor-pointer disabled:opacity-60"
            >
              {isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </Notice>
        )}
        {offer && status === 'expired' && (
          <Notice icon={<FiAlertCircle size={13} />} tone="amber">
            {isCreator
              ? 'This offer has expired. Send a new offer to continue.'
              : 'This offer is no longer available.'}
          </Notice>
        )}
        {offer && status === 'withdrawn' && (
          <Notice icon={<FiAlertCircle size={13} />} tone="zinc">
            {isCreator
              ? 'You withdrew this offer.'
              : 'This offer has been withdrawn and can no longer be accepted.'}
          </Notice>
        )}
        {/* TEMP-DISABLED: external payment notices hidden — chat only for now.
        {pendingSettlement?.waitingOnMe && (
          <Notice icon={<FiAlertCircle size={13} />} tone="amber">
            {partyName} recorded a {settlementAmount} payment outside Musicade. Confirm it if you{' '}
            {isLinkGenerator ? 'received' : 'sent'} it.
          </Notice>
        )}
        {pendingSettlement && !pendingSettlement.waitingOnMe && (
          <Notice icon={<FiClock size={13} />} tone="zinc">
            Waiting for {partyName} to confirm {settlementAmount}.
          </Notice>
        )}
        */}
        {showWaitingForLink && (
          <Notice icon={<FiClock size={13} />} tone="zinc">
            {gigOffer
              ? `Waiting for ${partyName} to request payment. You can keep chatting.`
              : `Waiting for ${partyName} to send the payment link.`}
          </Notice>
        )}
        {showPaymentRequested && activeLink && (
          <Notice icon={<FiClock size={13} />} tone="zinc">
            {gigOffer ? 'Payment requested' : 'Payment link sent'}
            {activeLink.amount != null ? ` · ${money(activeLink.amount, currency)}` : ''}. Waiting
            for {partyName} to pay.
          </Notice>
        )}
        {isPaid && (
          <Notice icon={<FiCheckCircle size={13} />} tone="green">
            {isLinkGenerator && gigOffer
              ? 'Payment complete. Your payout is on its way.'
              : 'Payment complete.'}
          </Notice>
        )}

        {hasPrimaryAction && (
          <div className="space-y-2 px-4 pt-4">
            {showRecipientActions && (
              <>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => open('changes')}
                    className={`${secondaryButton} text-white`}
                  >
                    <FiEdit3 size={13} />
                    Ask for changes
                  </button>
                  <button
                    type="button"
                    onClick={() => open('decline')}
                    className={`${secondaryButton} text-red-300 hover:text-red-200`}
                  >
                    <FiX size={14} />
                    Decline
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => open('accept')}
                  className={`${primaryButton} bg-[#22c55e] text-black hover:bg-[#2fd36a]`}
                >
                  <FiCheck size={15} className="stroke-[3]" />
                  Accept offer
                </button>
              </>
            )}
            {showRevise && (
              <button
                type="button"
                onClick={() => open('revise')}
                className={`${primaryButton} bg-[#1784FF] text-white hover:bg-[#2f91ff]`}
              >
                <FiEdit3 size={14} />
                Send Revised Offer
              </button>
            )}
            {showSendNew && (
              <button
                type="button"
                onClick={() => open('new')}
                className={`${primaryButton} bg-[#1784FF] text-white hover:bg-[#2f91ff]`}
              >
                <FiSend size={14} />
                Send New Offer
              </button>
            )}
            {/* TEMP-DISABLED: external payment confirm/reject hidden — chat only for now.
            {showSettlementActions && (
              <div className="flex gap-2">
                <Tooltip content={paymentTips.rejectSettlement}>
                  <button
                    type="button"
                    onClick={() => open('settlement')}
                    className={`${secondaryButton} text-red-300 hover:text-red-200`}
                  >
                    <FiX size={14} />
                    Reject
                  </button>
                </Tooltip>
                <Tooltip content={paymentTips.confirmSettlement}>
                  <button
                    type="button"
                    onClick={() => open('settlement')}
                    className={`${secondaryButton} border-emerald-500/30 text-emerald-300 hover:text-emerald-200`}
                  >
                    <FiCheck size={14} />
                    Confirm
                  </button>
                </Tooltip>
              </div>
            )}
            */}
            {showStripeSetup && (
              <Tooltip content={paymentTips.stripeSetup}>
                <button
                  type="button"
                  onClick={() => open('stripe')}
                  className={`${primaryButton} bg-[#1784FF] text-white hover:bg-[#2f91ff]`}
                >
                  <FiCreditCard size={14} />
                  Set Up Payment
                </button>
              </Tooltip>
            )}
            {showRequestPayment && (
              <Tooltip content={paymentTips.requestPayment}>
                <button
                  type="button"
                  onClick={() => open('request')}
                  className={`${primaryButton} bg-[#22c55e] text-black hover:bg-[#2fd36a]`}
                >
                  <FiCreditCard size={14} />
                  Request Payment
                </button>
              </Tooltip>
            )}
            {showPay && activeLink && (
              <Tooltip content={paymentTips.pay}>
                <button
                  type="button"
                  onClick={() => window.open(activeLink.url, '_blank', 'noopener,noreferrer')}
                  className={`${primaryButton} bg-[#22c55e] text-black hover:bg-[#2fd36a]`}
                >
                  <FiCreditCard size={14} />
                  {activeLinkTotal != null ? `Pay ${money(activeLinkTotal, currency)}` : 'Pay Now'}
                </button>
              </Tooltip>
            )}
          </div>
        )}

        {/* TEMP-DISABLED: external payment record link hidden — chat only for now.
        {showOffPlatformLink && (
          <div className="px-4 pt-3 text-center">
            <Tooltip content={paymentTips.recordOffPlatform}>
              <button
                type="button"
                onClick={() => open('offplatform')}
                className="text-[12px] font-medium text-zinc-400 underline-offset-2 transition hover:text-white hover:underline cursor-pointer"
              >
                Paid outside Musicade? Record it
              </button>
            </Tooltip>
          </div>
        )}
        */}

        <button
          type="button"
          onClick={() => open()}
          className="group mt-4 flex w-full items-center justify-between border-t border-white/[0.06] px-4 py-3 text-[13px] font-semibold text-zinc-300 transition hover:bg-white/[0.03] hover:text-white cursor-pointer"
        >
          View offer details
          <FiChevronRight
            size={16}
            className="text-zinc-500 transition group-hover:translate-x-0.5 group-hover:text-white"
          />
        </button>
      </div>
    </div>
  );
};
